import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { FormField, type FormFieldControlProps } from './FormField';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  /** Label text, threaded through FormField. */
  label?: string;
  /** Renders a `*` beside the label (threaded through FormField). */
  required?: boolean;
  /** Help text rendered below the control (threaded through FormField). */
  help?: string;
  /** Error text rendered below the control; sets aria-invalid (threaded through FormField). */
  error?: string;
}

/**
 * Textarea — a multiline text control composing FormField, matching Input's
 * look (BR-UI-TA-001..005, #14004).
 *
 * Same border/radius/focus-ring/error styling as Input, sized via the native
 * `rows` attribute with a sensible min-height floor and resize-vertical-only
 * (`resize-y`) so the control never shrinks below a usable height but never
 * grows wider than its container.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, required, help, error, className = '', rows = 4, maxLength, value, ...rest }, ref) => {
    const charCount = typeof value === 'string' ? value.length : 0;

    return (
      <FormField label={label} required={required} help={help} error={error}>
        {(controlProps: FormFieldControlProps) => (
          <>
            <textarea
              ref={ref}
              rows={rows}
              maxLength={maxLength}
              value={value}
              className={`w-full min-h-[6rem] resize-y rounded-md border px-3 py-2 text-sm transition-colors
                ${error ? 'border-red-500 focus:ring-red-500/50' : 'border-gray-300 focus:ring-indigo-500/50 focus:border-indigo-500'}
                focus:outline-none focus:ring-2
                ${className}`}
              {...controlProps}
              {...rest}
            />
            {maxLength !== undefined && (
              <p className="mt-1 text-xs text-gray-500 text-right">
                {charCount}/{maxLength}
              </p>
            )}
          </>
        )}
      </FormField>
    );
  },
);

Textarea.displayName = 'Textarea';
